import type { components } from "../../../api/generated";
import type {
  BankCardAccountOverview,
  BankCardCollectionOrder,
  BankCardCollectionOverviewPayload,
  BankCardCollectionResolvePayload,
  BankCardOverviewPaymentItem,
  CardPaymentAccountItem,
  CardPaymentAccountOverview,
  CardPaymentEReceiptApplyResult,
  CardPaymentEReceiptDownloadResult,
  CardPaymentEReceiptItem,
  CardPaymentOverviewPayload,
  CardPaymentRecordItem,
  PaymentRecord,
  PaymentRecordDetail,
  QrCollectionOrder,
  QrCollectionResolvePayload,
  RefundRecord,
} from "../types";

export type QrCollectionResolveResponseRaw =
  components["schemas"]["QrCollectionResolveSuccessResponse"];
export type BankCardCollectionResolveResponseRaw =
  components["schemas"]["BankCardCollectionResolveSuccessResponse"];
export type BankCardCollectionOverviewResponseRaw =
  components["schemas"]["BankCardCollectionOverviewSuccessResponse"];
export type CardPaymentOverviewResponseRaw =
  components["schemas"]["CardPaymentOverviewSuccessResponse"];
export type CreateCardPaymentAccountResponseRaw =
  components["schemas"]["CreateCardPaymentAccountSuccessResponse"];
export type CreateCardPaymentTransferResponseRaw =
  components["schemas"]["CreateCardPaymentTransferSuccessResponse"];
export type CardPaymentEReceiptApplyResponseRaw =
  components["schemas"]["CardPaymentEReceiptApplySuccessResponse"];
export type CardPaymentEReceiptQueryResponseRaw =
  components["schemas"]["CardPaymentEReceiptQuerySuccessResponse"];
export type CardPaymentEReceiptDownloadResponseRaw =
  components["schemas"]["CardPaymentEReceiptDownloadSuccessResponse"];
export type PaymentRecordDetailResponseRaw =
  components["schemas"]["PaymentRecordDetailSuccessResponse"];
export type CreateRefundResponseRaw =
  components["schemas"]["CreateRefundSuccessResponse"];

/** 将二维码收款契约映射为页面稳定类型。 */
export function mapQrCollectionResolvePayload(
  raw: components["schemas"]["QrCollectionResolvePayload"],
): QrCollectionResolvePayload {
  return {
    order: mapQrCollectionOrder(raw.order),
    payments: {
      items: (raw.payments.items ?? []).map(mapPaymentRecord),
      page: raw.payments.page,
      page_size: raw.payments.page_size,
      total: raw.payments.total,
    },
  };
}

/** 将银行卡号收款契约映射为页面稳定类型。 */
export function mapBankCardCollectionResolvePayload(
  raw: components["schemas"]["BankCardCollectionResolvePayload"],
): BankCardCollectionResolvePayload {
  return {
    order: mapBankCardCollectionOrder(raw.order),
    payments: {
      items: (raw.payments.items ?? []).map(mapPaymentRecord),
      page: raw.payments.page,
      page_size: raw.payments.page_size,
      total: raw.payments.total,
    },
  };
}

/** 将银行卡号收款总览契约映射为页面稳定类型。 */
export function mapBankCardCollectionOverviewPayload(
  raw: components["schemas"]["BankCardCollectionOverviewPayload"],
): BankCardCollectionOverviewPayload {
  return {
    account: mapBankCardAccountOverview(raw.account),
    payments: {
      items: (raw.payments.items ?? []).map(mapBankCardOverviewPaymentItem),
      page: raw.payments.page,
      page_size: raw.payments.page_size,
      total: raw.payments.total,
    },
  };
}

/** 将卡支付总览契约映射为页面稳定类型。 */
export function mapCardPaymentOverviewPayload(
  raw: components["schemas"]["CardPaymentOverviewPayload"],
): CardPaymentOverviewPayload {
  return {
    accounts: (raw.accounts ?? []).map(mapCardPaymentAccountItem),
    account: mapCardPaymentAccountOverview(raw.account),
    payments: {
      items: (raw.payments.items ?? []).map(mapCardPaymentRecordItem),
      page: raw.payments.page,
      page_size: raw.payments.page_size,
      total: raw.payments.total,
    },
  };
}

/** 将卡支付转账结果契约映射为页面稳定类型。 */
export function mapCardPaymentRecordItem(
  raw: components["schemas"]["CardPaymentRecordItem"],
): CardPaymentRecordItem {
  return {
    ...raw,
    provider_detail: (raw.provider_detail as Record<string, unknown> | undefined) ?? {},
  };
}

/** 将电子回单申请结果映射为页面稳定类型。 */
export function mapCardPaymentEReceiptApplyResult(
  raw: components["schemas"]["CardPaymentEReceiptApplyPayload"],
): CardPaymentEReceiptApplyResult {
  return {
    client_id: raw.client_id,
    status: raw.status,
    status_text: raw.status_text ?? "",
  };
}

/** 将电子回单条目映射为页面稳定类型。 */
export function mapCardPaymentEReceiptItem(
  raw: components["schemas"]["CardPaymentEReceiptItem"],
): CardPaymentEReceiptItem {
  return {
    rcpt_num: raw.rcpt_num,
    date: raw.date ?? "",
    amount: raw.amount ?? "",
    pay_account_no: raw.pay_account_no ?? "",
    receive_account_no: raw.receive_account_no ?? "",
    summary: raw.summary ?? "",
    bank_serial_no: raw.bank_serial_no ?? "",
  };
}

/** 将电子回单下载结果映射为页面稳定类型。 */
export function mapCardPaymentEReceiptDownloadResult(
  raw: components["schemas"]["CardPaymentEReceiptDownloadPayload"],
): CardPaymentEReceiptDownloadResult {
  return {
    rcpt_num: raw.rcpt_num,
    file_name: raw.file_name,
    file_content: raw.file_content,
    size: raw.size ?? "",
  };
}

/** 将付款详情契约映射为页面稳定类型。 */
export function mapPaymentRecordDetail(
  raw: components["schemas"]["PaymentRecordDetailPayload"],
): PaymentRecordDetail {
  return {
    payment: mapPaymentRecord(raw.payment),
    refunds: (raw.refunds ?? []).map(mapRefundRecord),
    provider_detail: raw.provider_detail ?? {},
  };
}

/** 将退款契约映射为页面稳定类型。 */
export function mapRefundRecord(
  raw: components["schemas"]["RefundRecordItem"],
): RefundRecord {
  return { ...raw };
}

/** 将二维码收款订单契约映射为页面稳定类型。 */
function mapQrCollectionOrder(
  raw: components["schemas"]["QrCollectionOrder"],
): QrCollectionOrder {
  return { ...raw };
}

/** 将中信收款卡订单契约映射为页面稳定类型。 */
function mapBankCardCollectionOrder(
  raw: components["schemas"]["BankCardCollectionOrder"],
): BankCardCollectionOrder {
  return { ...raw };
}

/** 将固定结算卡账户契约映射为页面稳定类型。 */
function mapBankCardAccountOverview(
  raw: components["schemas"]["BankCardAccountOverview"],
): BankCardAccountOverview {
  return { ...raw };
}

/** 将卡支付账户列表项契约映射为页面稳定类型。 */
function mapCardPaymentAccountItem(
  raw: components["schemas"]["CardPaymentAccountItem"],
): CardPaymentAccountItem {
  return { ...raw };
}

/** 将卡支付账户契约映射为页面稳定类型。 */
function mapCardPaymentAccountOverview(
  raw: components["schemas"]["CardPaymentAccountOverview"],
): CardPaymentAccountOverview {
  return { ...raw };
}

/** 将固定结算卡入账明细契约映射为页面稳定类型。 */
function mapBankCardOverviewPaymentItem(
  raw: components["schemas"]["BankCardOverviewPaymentItem"],
): BankCardOverviewPaymentItem {
  return {
    ...raw,
    provider_detail: (raw.provider_detail as Record<string, unknown> | undefined) ?? {},
  };
}

/** 将付款流水契约映射为页面稳定类型。 */
function mapPaymentRecord(
  raw: components["schemas"]["PaymentRecordItem"],
): PaymentRecord {
  return { ...raw };
}
